import { z } from 'zod';
import { isoDateSchema } from './common.js';

/**
 * Wave 15.2 — Cronograma físico-financeiro.
 *
 * The Brazilian construction standard S-curve has two overlapping
 * dimensions:
 *
 *   físico     — planned vs actual *progress %* over time.
 *   financeiro — planned vs committed vs realized *spend* over time.
 *
 * Builder snapshots physical progress in `project_progress_snapshots`
 * (written weekly by the cron + on stage transitions). Financial data
 * lives in `purchase_orders` (commitment) and `payments` (realized
 * spend). The planned financial curve is a straight-line allocation of
 * `projects.budget_cents` across the project's start → estimated end
 * window — when we have a more granular `schedule_of_values` table we
 * can swap this for real per-bucket planned spend without changing the
 * contract.
 *
 * Buckets
 * -------
 * Each bucket has a `key` (ISO, e.g. `2026-04`) and `endDate` (last day
 * of the bucket). The web client draws the four series on a shared X
 * axis over the bucket index. Cumulative values (not per-bucket deltas)
 * — that's what makes it an S-curve.
 */

export const cronogramaGranularitySchema = z.enum(['month', 'week']);
export type CronogramaGranularity = z.infer<typeof cronogramaGranularitySchema>;

export const cronogramaQuerySchema = z.object({
  granularity: cronogramaGranularitySchema.optional(),
});
export type CronogramaQuery = z.infer<typeof cronogramaQuerySchema>;

export const cronogramaBucketSchema = z.object({
  /** `YYYY-MM` for month granularity, ISO date of the bucket end for week. */
  key: z.string(),
  /** Inclusive last day of this bucket. */
  endDate: isoDateSchema,
  /** 0..100. From progress_snapshots.planned_percent at or before endDate. */
  plannedPhysicalPercent: z.number(),
  /** 0..100. From progress_snapshots.actual_percent at or before endDate. */
  actualPhysicalPercent: z.number(),
  /** Cumulative planned spend, straight-line from project start → end. */
  plannedFinancialCents: z.number().int().nonnegative(),
  /** Cumulative PO total_cents issued on or before endDate. */
  committedFinancialCents: z.number().int().nonnegative(),
  /** Cumulative settled payment outflows on or before endDate. */
  paidFinancialCents: z.number().int().nonnegative(),
});
export type CronogramaBucket = z.infer<typeof cronogramaBucketSchema>;

export const cronogramaProjectSchema = z.object({
  id: z.string(),
  name: z.string(),
  startDate: isoDateSchema.nullable(),
  estimatedEndDate: isoDateSchema.nullable(),
  budgetCents: z.number().int().nonnegative().nullable(),
});
export type CronogramaProject = z.infer<typeof cronogramaProjectSchema>;

export const cronogramaReportSchema = z.object({
  project: cronogramaProjectSchema,
  granularity: cronogramaGranularitySchema,
  buckets: z.array(cronogramaBucketSchema),
  totals: z.object({
    /** projects.budget_cents or 0 if unset. */
    budgetCents: z.number().int().nonnegative(),
    /** Sum of non-cancelled PO total_cents to date. */
    committedCents: z.number().int().nonnegative(),
    /** Sum of settled payment outflows to date. */
    paidCents: z.number().int().nonnegative(),
  }),
  generatedAt: z.string(),
});
export type CronogramaReport = z.infer<typeof cronogramaReportSchema>;
